import { extractProjectIdFromPathname } from 'util/resolveCurrentProjectId'
import {
  requiresDesignTab,
  requiresInverterAdded,
  requiresPanels,
  requiresSelectedSystem,
  requiresSingleInverterType,
  requiresSinglePanelType,
  requiresStudioLoaded,
} from '../actionPermissions'

export const isInDesignTab = (pathname: string): { isInDesignTab: boolean; projectId?: number } => {
  const isInDesign = /^\/projects\/(\d+)\/design/.test(pathname)
  return {
    isInDesignTab: isInDesign,
    projectId: isInDesign ? extractProjectIdFromPathname(pathname) : undefined,
  }
}

export const isEditorReady = (): boolean => {
  return !!(window.editor && window.editor.scene)
}

export const getSelectedSystem = (): any => {
  if (!window.editor || !window.editor.scene) {
    return null
  }
  return window.editor.selectedSystem || null
}

export interface ActionValidationResult {
  isValid: boolean
  message: string
  projectId?: number
}

export function validateActionRequirements(
  actionType: string,
  pathname: string,
  selectedSystem?: any
): ActionValidationResult {
  let projectId: number | undefined

  if (requiresDesignTab(actionType)) {
    const { isInDesignTab: inDesignTab, projectId: extractedProjectId } = isInDesignTab(pathname)
    projectId = extractedProjectId

    if (!inDesignTab) {
      return {
        isValid: false,
        message: 'This action requires the design tab. Please navigate to the project design tab first.',
        projectId,
      }
    }
  }

  if (requiresStudioLoaded(actionType)) {
    if (!isEditorReady()) {
      return {
        isValid: false,
        message: 'Design tab is loading. Please wait for it to fully load.',
        projectId,
      }
    }
  }

  if (requiresSelectedSystem(actionType)) {
    if (!isEditorReady()) {
      return {
        isValid: false,
        message: 'Editor is not available. Please open the design tab first.',
      }
    }

    const system = selectedSystem !== undefined ? selectedSystem : getSelectedSystem()

    if (!system) {
      return {
        isValid: false,
        message: 'Please select a system in the design before saving it as a template.',
        projectId,
      }
    }

    if (requiresInverterAdded(actionType)) {
      if (system.inverters().length === 0) {
        return {
          isValid: false,
          message: 'Auto String requires at least one inverter to be added to the system.',
          projectId,
        }
      }
    }

    if (requiresPanels(actionType)) {
      if (system.moduleQuantity() === 0) {
        // Get action-specific error message
        let message: string
        if (actionType === 'runAutoString') {
          message = 'Auto String requires panels to be placed in the system.'
        } else if (actionType === 'runBatteryDesignAssistant') {
          message = 'Battery recommendations require panels to be placed in the system.'
        } else {
          message = 'This action requires panels to be placed in the system.'
        }
        return {
          isValid: false,
          message,
          projectId,
        }
      }
    }

    if (requiresSinglePanelType(actionType)) {
      if (system.hasMultipleModuleTypes()) {
        return {
          isValid: false,
          message:
            'Auto Stringing is not available as multiple panels are present on system. Please manually string panel groups.',
          projectId,
        }
      }
    }

    if (requiresSingleInverterType(actionType)) {
      const inverters = system.inverters()
      if (inverters.length > 1 && inverters.some((inverter) => inverter.code !== inverters[0].code)) {
        return {
          isValid: false,
          message: 'Auto String is only available when a single inverter type is used.',
          projectId,
        }
      }
    }
  }

  return { isValid: true, message: '' }
}

export function validateActionRequirementsOrThrow(actionType: string, pathname: string, selectedSystem?: any): void {
  const validation = validateActionRequirements(actionType, pathname, selectedSystem)
  if (!validation.isValid) {
    throw new Error(validation.message)
  }
}
